import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import * as services from "../../services/services";

function getStoredToken(key) {
  const value = localStorage.getItem(key);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (err) {
    return value;
  }
}

function getCurrentUser() {
  try {
    const token = getStoredToken("accessToken");
    return token ? jwtDecode(token) : null;
  } catch (err) {
    return null;
  }
}

const emptyUser = {
  username: "",
  title: "",
  name: "",
  lastname: "",
  idCard: "",
  license_no: "",
  phoneNumber: "",
  image: "",
  imageFile: null,
};

export default function ProfileEditForm({ currentUser }) {
  const authUser = useMemo(() => currentUser || getCurrentUser(), [currentUser]);
  const userId = authUser?.id;

  const [user, setUser] = useState(emptyUser);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    rePassword: "",
  });

  useEffect(() => {
    let active = true;

    const fetchProfile = async () => {
      if (!userId) {
        toast.error("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await services.getUserById(userId);

        if (!active) return;

        if (res.data.success) {
          const data = res.data.data || {};
          setUser({ ...emptyUser, ...data, imageFile: null });
          setImagePreview(data.image || null);
        } else {
          toast.error(res.data.error || "โหลดข้อมูลไม่สำเร็จ");
        }
      } catch (err) {
        if (active) toast.error("โหลดข้อมูลไม่สำเร็จ");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProfile();

    return () => {
      active = false;
    };
  }, [userId]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files?.[0]) {
      const file = files[0];
      setImagePreview(URL.createObjectURL(file));
      setUser((prev) => ({ ...prev, imageFile: file }));
      return;
    }

    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = (extra = {}) => {
    const payload = {
      ...user,
      ...extra,
    };
    delete payload.imageFile;
    return payload;
  };

  const handleSubmit = async () => {
    if (!userId) {
      toast.error("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!user.name?.trim() || !user.lastname?.trim()) {
      toast.error("กรุณากรอกชื่อและนามสกุล");
      return;
    }

    try {
      setSaving(true);
      let imageUrl = user.image || "";

      if (user.imageFile) {
        const uploadRes = await services.uploadImage(user.imageFile, "user");
        imageUrl = uploadRes.data.url;
      }

      await services.updateUser({ id: userId }, buildPayload({ image: imageUrl }));

      setUser((prev) => ({ ...prev, image: imageUrl, imageFile: null }));
      setImagePreview(imageUrl || null);
      toast.success("บันทึกสำเร็จ");
    } catch (err) {
      toast.error(err?.response?.data?.error || "บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPasswordDialog = () => {
    setPasswordForm({ password: "", rePassword: "" });
    setShowPassword(false);
    setShowRePassword(false);
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    if (passwordSaving) return;
    setPasswordDialogOpen(false);
    setPasswordForm({ password: "", rePassword: "" });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePassword = async () => {
    if (!userId) {
      toast.error("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!passwordForm.password) {
      toast.error("กรุณากรอกรหัสผ่านใหม่");
      return;
    }

    if (passwordForm.password.length < 8) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }

    if (passwordForm.password !== passwordForm.rePassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }

    try {
      setPasswordSaving(true);
      await services.updateUser(
        { id: userId },
        buildPayload({ password: passwordForm.password }),
      );

      toast.success("เปลี่ยนรหัสผ่านสำเร็จ");
      handleClosePasswordDialog();
    } catch (err) {
      toast.error(err?.response?.data?.error || "เปลี่ยนรหัสผ่านไม่สำเร็จ");
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 3 }}>
      <Toaster position="bottom-left" />

      <Typography variant="h5" fontWeight={600} mb={3}>
        แก้ไขข้อมูลผู้ใช้
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
          gap: 4,
        }}
      >
        <Box
          sx={{
            border: "2px dashed #ddd",
            borderRadius: 3,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            minHeight: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fafafa",
          }}
          onClick={() => document.getElementById("profile-image")?.click()}
        >
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="preview"
              style={{
                width: 160,
                height: 160,
                objectFit: "cover",
                borderRadius: 12,
              }}
            />
          ) : (
            <Typography color="text.secondary">คลิกเพื่ออัปโหลดรูป</Typography>
          )}

          <input
            id="profile-image"
            type="file"
            hidden
            accept="image/*"
            name="image"
            onChange={handleChange}
          />
        </Box>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <TextField
                label="Email"
                name="username"
                value={user.username || ""}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenPasswordDialog}
                fullWidth
              >
                เปลี่ยนรหัสผ่าน
              </Button>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="คำนำหน้า"
                name="title"
                value={user.title || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="ชื่อ"
                name="name"
                value={user.name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="นามสกุล"
                name="lastname"
                value={user.lastname || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="เลขบัตรประชาชน"
                name="idCard"
                value={user.idCard || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="เลขที่ใบอนุญาต"
                name="license_no"
                value={user.license_no || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="เบอร์โทรศัพท์"
                name="phoneNumber"
                value={user.phoneNumber || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={passwordDialogOpen}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>เปลี่ยนรหัสผ่าน</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="รหัสผ่านใหม่"
            name="password"
            type={showPassword ? "text" : "password"}
            value={passwordForm.password}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
            error={passwordForm.password.length > 0 && passwordForm.password.length < 8}
            helperText={
              passwordForm.password.length > 0 && passwordForm.password.length < 8
                ? "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="ยืนยันรหัสผ่าน"
            name="rePassword"
            type={showRePassword ? "text" : "password"}
            value={passwordForm.rePassword}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
            error={
              passwordForm.rePassword.length > 0 &&
              passwordForm.rePassword !== passwordForm.password
            }
            helperText={
              passwordForm.rePassword.length > 0 &&
              passwordForm.rePassword !== passwordForm.password
                ? "รหัสผ่านไม่ตรงกัน"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowRePassword((prev) => !prev)}>
                    {showRePassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={passwordSaving}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePassword}
            disabled={passwordSaving}
          >
            {passwordSaving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
